/**
 * Ordering and state rules both import UIs render from, so web and mobile
 * cannot drift on where a category sits in the picker or which step of a
 * running job reads as done.
 */
import { EXPENSE_TAXONOMY } from '../categoryPrediction/taxonomy';

export type ImportChecklistState = 'done' | 'active' | 'pending' | 'failed';

/**
 * Which of an ordered list of steps are behind, at, or ahead of the one a
 * job reports. A finished job ticks every step; a failed one marks the step
 * it stopped on. A step the order does not know (or none at all) means the
 * job has not reached the first one yet.
 */
export function checklistStates<K extends string>(
  order: readonly K[],
  current: string | null | undefined,
  outcome: 'running' | 'done' | 'failed'
): Record<K, ImportChecklistState> {
  const at =
    current === undefined || current === null
      ? -1
      : order.indexOf(current as K);
  const states = {} as Record<K, ImportChecklistState>;
  order.forEach((key, index) => {
    if (outcome === 'done' || index < at) states[key] = 'done';
    else if (index === at)
      states[key] = outcome === 'failed' ? 'failed' : 'active';
    else states[key] = 'pending';
  });
  return states;
}

/** The server's import steps in the order it runs them; `done` is not a step. */
export const IMPORT_STEP_ORDER = [
  'group',
  'placeholders',
  'expenses',
  'settlements',
  'balances',
] as const;

/**
 * Every taxonomy path a category override may point at: categories A–Z by
 * their label in the user's language, each followed by its subcategories,
 * also A–Z, with "other" pinned last at both levels. Sorted on labels rather
 * than keys so "Drinks" and "Bebidas" both land where the reader expects them.
 */
export function sortedTaxonomyPaths(
  label: (path: string) => string,
  language: string
): string[] {
  const collator = new Intl.Collator(language);
  const isOther = (path: string) => path.split('/').pop() === 'other';
  const byLabel = (a: string, b: string) => {
    if (isOther(a) !== isOther(b)) return isOther(a) ? 1 : -1;
    return collator.compare(label(a), label(b));
  };
  return Object.entries(EXPENSE_TAXONOMY)
    .map(([category, subcategories]) => ({
      category,
      paths: subcategories
        .map(subcategory => `${category}/${subcategory}`)
        .sort(byLabel),
    }))
    .sort((a, b) => byLabel(a.category, b.category))
    .flatMap(group => [group.category, ...group.paths]);
}
